import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase URL and Anon Key are required');
}

// Initialize the Supabase client
const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Auth helper functions
const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) throw error;

    // Fetch user profile after successful sign in
    if (data.user) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', data.user.id)
        .single();

      return { data: { ...data, profile }, error: null };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Sign in error:', error);
    return { data: null, error };
  }
};

const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    return { error };
  } catch (error) {
    console.error('Sign out error:', error);
    return { error };
  }
};

const getCurrentUser = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;

    if (user) {
      // Fetch user profile
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileError) throw profileError;

      return { user: { ...user, profile }, error: null };
    }

    return { user: null, error: null };
  } catch (error) {
    // Handle auth session missing gracefully
    if (error && (error as any).message === 'Auth session missing!') {
      return { user: null, error: null };
    }
    return { user: null, error };
  }
};

// Storage helper functions
const uploadImage = async (file: File, path: string) => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    const filePath = `${path}/${fileName}`;

    const { data, error } = await supabase.storage
      .from('properties')
      .upload(filePath, file);

    if (error) {
      throw error;
    }

    const { data: publicURL } = supabase.storage
      .from('properties')
      .getPublicUrl(filePath);

    return { data: { path: filePath, url: publicURL.publicUrl }, error: null };
  } catch (error) {
    console.error('Error uploading image:', error);
    return { data: null, error };
  }
};

const deleteImage = async (path: string) => {
  try {
    const { data, error } = await supabase.storage
      .from('properties')
      .remove([path]);

    return { data, error };
  } catch (error) {
    console.error('Error deleting image:', error);
    return { data: null, error };
  }
};

export {
  supabase,
  signIn,
  signOut,
  getCurrentUser,
  uploadImage,
  deleteImage
};